import { Anchor, Button, Select, Stack, Tabs, Text, Title } from '@mantine/core';
import { useState } from 'react';
import { Link } from 'react-router';
import { CrudSection } from '../components/CrudSection';
import { EntityLabel, selectData } from '../components/EntityField';
import { useEntityIndex } from '../hooks/useEntityIndex';
import { useBaseCurrency, useLocationId } from '../hooks/useSeedLocation';
import { useTabInUrl } from '../hooks/useTabInUrl';
import { enumOptions } from '../lib/enums';
import { formatMoney, formatQty, formatRateBp } from '../lib/format';
import { suggestPlural } from '../lib/units';
import { CountAreaButton } from './inventory/CountAreaButton';
import { CountsWaiting } from './inventory/CountsWaiting';
import { LogWasteButton } from './inventory/LogWasteButton';
import { MoveStockButton } from './inventory/MoveStockButton';
import { RecipeForm } from './inventory/RecipeForm';
import { RunningLowSection } from './inventory/RunningLowSection';
import { SalesNotMovingStock } from './inventory/SalesNotMovingStock';
import { StorageLayout } from './inventory/StorageLayout';
import {
  ITEMS_PATH,
  LEDGER_PATH,
  marginTone,
  RECIPES_PATH,
  type RecipeRow,
  STOCK_PATH,
  useReasonNames,
  useStockItems,
} from './inventory/stock';
import { UnitConversions } from './inventory/UnitConversions';
import { WhatCanIMake } from './inventory/WhatCanIMake';

/** "2.5 bottle" — a bare number in a stock table is unreadable without its unit. */
function qtyWithUnit(row: Record<string, unknown>): string {
  return formatQty(row.qty as number | null | undefined, row.unit ? String(row.unit) : undefined);
}

/**
 * What a recipe produces — the dish itself, not the kind of thing it is.
 *
 * This column used to render the target's TYPE, so a screen full of recipes said "A dish or drink
 * on the menu" forty times over and never once named the dish. The identifier that would have
 * answered it was on the row all along.
 */
function RecipeMakes({ row }: { row: Record<string, unknown> }) {
  // Three kinds now: a size on the menu, a batch prepped in house, and an extra someone adds or
  // leaves off ("Add bacon"). Each resolves against its own list — a modifier option id looked up
  // among the sizes comes back as "Not found", which is how the column names the wrong thing
  // rather than nothing.
  const entity =
    row.targetType === 'variant'
      ? 'variant'
      : row.targetType === 'modifierOption'
        ? 'modifierOption'
        : 'inventoryItem';
  return <EntityLabel entity={entity} id={row.targetId} />;
}

/**
 * The tabs, in the address. Running low is the one a manager is sent to — "order these from them"
 * is the morning's work — and it could only be reached by opening Stock items and clicking across.
 */
const TABS = ['items', 'onhand', 'canmake', 'storage', 'reorder', 'recipes', 'ledger'] as const;

export function InventoryPage() {
  const locationId = useLocationId();
  const [tab, setTab] = useTabInUrl(TABS, 'items');
  const currency = useBaseCurrency();
  const stock = useStockItems();
  const areas = useEntityIndex('storageLocation');
  const reasonName = useReasonNames();

  /** Which shelf the on-hand table is showing. Empty means every area at once. */
  const [area, setArea] = useState<string | null>(null);
  /** Which item's history the movements list is showing. */
  const [movementsItem, setMovementsItem] = useState<string | null>(null);
  /** The recipe open for changing, `'new'` while one is being written, null when nothing is open. */
  const [editingRecipe, setEditingRecipe] = useState<RecipeRow | 'new' | null>(null);

  const items = useEntityIndex('inventoryItem');

  /** What one shelf holds, valued at what the bar has been paying for it. */
  const worthOf = (row: Record<string, unknown>): string => {
    const qty = row.qtyOnHand;
    if (typeof qty !== 'number') return '—';
    return formatMoney(Math.round(qty * stock.costOf(row.inventoryItemId)), currency);
  };

  return (
    <>
      <Title order={3} mb="md">
        Stock &amp; recipes
      </Title>
      <Tabs value={tab} onChange={setTab} keepMounted={false}>
        <Tabs.List mb="md">
          <Tabs.Tab value="items">Stock items</Tabs.Tab>
          <Tabs.Tab value="onhand">On hand</Tabs.Tab>
          {/* Beside "On hand" because it is the same question one step further on: that tab says
              how much of everything there is, and nobody serves a shelf. */}
          <Tabs.Tab value="canmake">What can I make</Tabs.Tab>
          {/* One tab, not the two it replaced: rooms and the places inside them were two tables
              of the same layout, and neither could be read without the other. */}
          <Tabs.Tab value="storage">Where it is kept</Tabs.Tab>
          <Tabs.Tab value="reorder">Running low</Tabs.Tab>
          <Tabs.Tab value="recipes">Recipes</Tabs.Tab>
          <Tabs.Tab value="ledger">Movements</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="items">
          <CrudSection
            title="Stock items"
            entityNoun="stock item"
            description="Everything you buy and count — bottles, produce, packaging, cleaning supplies. The cost shown is the average you have actually been paying."
            listPath={ITEMS_PATH}
            updatePathTemplate={`${ITEMS_PATH}/{id}`}
            defaults={{ locationId }}
            rowLabel={(r) => String(r.nameEn ?? 'this item')}
            emptyMessage="Nothing on the stock list yet. Add the things you buy and count."
            columns={[
              // Retired items are marked on the name rather than in a column of their own: a
              // column that is blank on all but two rows costs every reader a header to skip.
              {
                accessor: 'nameEn',
                title: 'Item',
                // The name is the way in to the item itself, the same as a person's name on the
                // staff list. Everything you would do to one thing you buy — count it, log what
                // was thrown away, see who sells it — lives there rather than in a header button
                // that opens a blank form and asks which item you meant.
                render: (r) => (
                  <>
                    <Anchor component={Link} to={`/inventory/${String(r.id)}`} fw={600}>
                      {String(r.nameEn ?? '')}
                    </Anchor>
                    {r.active === false && (
                      <Text size="xs" c="dimmed">
                        No longer stocked
                      </Text>
                    )}
                  </>
                ),
              },
              { accessor: 'category', title: 'Category', enumGroup: 'inventoryCategory' },
              { accessor: 'stockUnit', title: 'Counted in' },
              {
                accessor: 'avgCostBaseMinor',
                title: 'Cost per unit',
                /* Zero here is an ABSENCE, not a price. The column under a blurb saying "the
                   average you have actually been paying" printed "$ 0.00" against a can nobody has
                   ever bought — the same sentence the recipe screen one tab away now refuses to
                   say, and the reason a rum and coke read as costing $0.72 with 87% left. The
                   backend states the rule outright (`NO_PRICE_ON_FILE` in `inventory/costing.ts`):
                   nothing has ever been delivered, so there is no average to show. */
                render: (r) =>
                  r.avgCostBaseMinor ? (
                    formatMoney(r.avgCostBaseMinor as number, currency)
                  ) : (
                    <Text size="sm" c="orange.7">
                      Never bought
                    </Text>
                  ),
              },
              { accessor: 'reorderPoint', title: 'Reorder at' },
              { accessor: 'parLevel', title: 'Aim to hold' },
            ]}
            fields={[
              { name: 'nameEn', label: 'Name', type: 'text', required: true },
              {
                name: 'nameEs',
                label: 'Name in Spanish',
                type: 'text',
                nullable: true,
                description: 'Used for staff who work in Spanish',
              },
              {
                name: 'category',
                label: 'Category',
                type: 'select',
                required: true,
                options: enumOptions('inventoryCategory'),
              },
              {
                name: 'stockUnit',
                label: 'Counted in',
                type: 'text',
                required: true,
                placeholder: 'bottle',
                description: 'How you count ONE of it when taking stock — bottle, kilo, litre, case',
              },
              /*
               * The plural, asked for the first time a word is used and never again.
               *
               * Quantities used to print the unit exactly as typed, so a short delivery read "3
               * bottle short of the 10 bottle ordered". Adding an "s" would have fixed that and
               * produced "2 kgs" and "3 Ls" on the units this bar uses most — the unit is free
               * text, and the measures do not change. So the platform asks.
               *
               * IT DOES NOT INTERROGATE. The box fills itself in from "Counted in" as that is
               * typed — with what the business has already said about the word, or a plain
               * suggestion where nobody has said anything — so the fortieth item counted in
               * bottles arrives answered and the first one counted in rings arrives with "rings"
               * to read and correct. Nothing blocks the save: cleared, or wrong before anybody
               * noticed, every quantity prints the singular, which is exactly what happened before
               * this existed. `lib/units.ts` holds the rule and the reasoning.
               */
              {
                name: 'stockUnitPlural',
                label: 'More than one',
                type: 'text',
                nullable: true,
                placeholder: 'bottles',
                suggest: (values) => suggestPlural(values.stockUnit as string | undefined),
                description:
                  'What you would call several of them, filled in for you and worth a glance. Several kg are still kg; several bottle are bottles. Asked once for each unit, not for each item.',
              },
              /*
               * Which shelf a sale comes off.
               *
               * The column has existed since the stock tables were written and no screen has ever
               * set it. That was survivable while nothing depleted stock; it stopped being when
               * selling a drink started taking the rum out of the bottle. A pour comes off the
               * item's default area, or the single area it is held in when there is only one — and
               * an item spread over two with no default is refused, because guessing which of them
               * the drink came out of is how a count stops balancing. The refusal reads "kept in
               * more than one area and none is its default", which was, until this field, a
               * sentence naming a box the owner could not open.
               *
               * Optional on purpose: a bar that keeps its limes in one fridge and nowhere else does
               * not need to answer, and making this required would put a question on every item to
               * cover the few that need it.
               */
              {
                name: 'defaultStorageLocationId',
                label: 'Where a sale comes off',
                type: 'entity',
                resource: 'storageLocation',
                nullable: true,
                description:
                  'Only needed when you keep it in more than one area — say which one a drink or a dish is poured or cooked from. Leave empty otherwise.',
              },
              {
                name: 'parLevel',
                label: 'Aim to hold',
                type: 'number',
                nullable: true,
                description: 'How much you like to have on hand when fully stocked',
              },
              {
                name: 'reorderPoint',
                label: 'Reorder at',
                type: 'number',
                nullable: true,
                description: 'Drop to this and the item appears on the Running low list',
              },
              // Without this an item you stop buying can never be taken out of the way, so it goes
              // on being offered in every waste form and on every count sheet for good.
              {
                name: 'active',
                label: 'Still buying it',
                type: 'boolean',
                defaultValue: true,
                description: 'Turn this off for something you no longer stock, to keep it off count sheets',
              },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="onhand">
          {/* Above the figures, because a count waiting to be approved is the reason one of the
              figures below is wrong — and because the area it covers cannot be counted again
              until it is dealt with. Renders nothing at all on the days nothing is stuck. */}
          <CountsWaiting currency={currency} />
          <CrudSection
            title="On hand"
            description="What the books say is on the shelf right now, area by area. A figure that looks wrong is put right by counting that area again, by logging what was thrown away, or by recording a move that never got written down."
            listPath={STOCK_PATH}
            listQuery={area ? { storageLocationId: area } : undefined}
            emptyMessage="Nothing on hand yet. Figures appear here once stock is received or counted."
            actions={
              <>
                {/* One list of every item in every area is unreadable by the time a bar has four
                    areas: counting the walk-in means reading past the bar and the dry store. */}
                <Select
                  size="sm"
                  placeholder="Every area"
                  data={selectData(areas.options)}
                  value={area}
                  onChange={setArea}
                  clearable
                  searchable
                  comboboxProps={{ withinPortal: true }}
                  style={{ width: 210 }}
                />
                <LogWasteButton />
                <MoveStockButton />
                <CountAreaButton currency={currency} />
              </>
            }
            // A stock row is one item in one area and carries no id of its own, so the table is
            // given a stable key rather than left to collide on `undefined`. Sorted by name because
            // the endpoint returns them in the order stock first moved, which is nobody's order.
            extractList={(d) =>
              (Array.isArray(d) ? (d as Record<string, unknown>[]) : [])
                .map(
                  (r): Record<string, unknown> => ({
                    ...r,
                    id: `${String(r.inventoryItemId)}:${String(r.storageLocationId)}`,
                  }),
                )
                .sort((a, b) =>
                  stock.nameOf(a.inventoryItemId).localeCompare(stock.nameOf(b.inventoryItemId)),
                )
            }
            columns={[
              {
                accessor: 'inventoryItemId',
                title: 'Item',
                // A link rather than a row of buttons: the list is for scanning, and everything
                // you would do to one of these figures is on the item's own page, which arrives
                // knowing both the item and the shelf.
                render: (r) => (
                  <Anchor component={Link} to={`/inventory/${String(r.inventoryItemId)}`} fw={500} size="sm">
                    {stock.nameOf(r.inventoryItemId) || '—'}
                  </Anchor>
                ),
              },
              { accessor: 'storageLocationId', title: 'Area', reference: 'storageLocation' },
              {
                accessor: 'qtyOnHand',
                title: 'On hand',
                // Less than nothing on a shelf is not a rounding quirk — it means stock left that
                // was never recorded arriving, and it is the one figure here worth chasing.
                render: (r) => {
                  const qty = r.qtyOnHand;
                  if (typeof qty !== 'number') return '—';
                  const reading = formatQty(qty, stock.unitOf(r.inventoryItemId));
                  return qty < 0 ? (
                    <Text size="sm" c="red.7" fw={600}>
                      {reading}
                    </Text>
                  ) : (
                    reading
                  );
                },
              },
              { accessor: 'worth', title: 'Worth', render: worthOf },
              { accessor: 'updatedAt', title: 'Last changed', unit: 'datetime', noWrap: true },
            ]}
          />
        </Tabs.Panel>

        <Tabs.Panel value="canmake">
          <WhatCanIMake />
        </Tabs.Panel>

        <Tabs.Panel value="storage">
          <StorageLayout locationId={locationId} currency={currency} />
        </Tabs.Panel>

        <Tabs.Panel value="reorder">
          {/* Was a flat table that worked out a shortfall and then could not say who sells it,
              under a description that told the reader to go and put an order together somewhere
              else. Grouped by vendor now, and each group opens an order to that vendor. */}
          <RunningLowSection currency={currency} locationId={locationId} />
        </Tabs.Panel>

        <Tabs.Panel value="recipes">
          {/* Above the recipes rather than in a tab of its own, because every one of these is
              fixed by writing or correcting a recipe — the list and the repair are one screen.
              It renders nothing at all when nothing is wrong. */}
          <SalesNotMovingStock />
          <CrudSection
            title="Recipes"
            description="What each dish, drink and prep batch uses, and what it costs to make at what you are currently paying. Change a recipe and the old one is kept, so past sales still show what they really cost."
            listPath={RECIPES_PATH}
            emptyMessage="No recipes yet. Add one and this will show what each dish costs to make."
            actions={
              <Button size="sm" onClick={() => setEditingRecipe('new')}>
                Add a recipe
              </Button>
            }
            columns={[
              { accessor: 'name', title: 'Recipe' },
              { accessor: 'targetId', title: 'Makes', render: (r) => <RecipeMakes row={r} /> },
              {
                accessor: 'yieldQty',
                title: 'One batch makes',
                render: (r) =>
                  formatQty(
                    r.yieldQty as number | null | undefined,
                    r.yieldUnit ? String(r.yieldUnit) : undefined,
                  ),
              },
              {
                accessor: 'costBaseMinor',
                title: 'Costs to make',
                render: (r) => {
                  // Null means the roll-up could not be completed, which is a different thing
                  // from a dish that costs nothing.
                  if (typeof r.costBaseMinor !== 'number') {
                    return (
                      <Text size="sm" c="dimmed">
                        Cannot be worked out
                      </Text>
                    );
                  }
                  // A third state, and the one this column used to hide: the figure is right about
                  // what it counted and short by an ingredient nothing has ever been paid for. It
                  // read as a finished plate cost, and the margin beside it as a fact.
                  const short = typeof r.uncostedComponents === 'number' ? r.uncostedComponents : 0;
                  return (
                    <Stack gap={0}>
                      {/* An extra that only leaves things out costs a NEGATIVE amount, which is
                          true and unreadable as a minus sign in a money column — a manager
                          scanning for "what does this cost" adds it on. Said as what it does. */}
                      {r.costBaseMinor < 0 ? (
                        <Text size="sm">Takes off {formatMoney(-r.costBaseMinor, currency)}</Text>
                      ) : (
                        <Text size="sm">{formatMoney(r.costBaseMinor, currency)}</Text>
                      )}
                      {short > 0 && (
                        <Text size="xs" c="orange.7">
                          at least — {short === 1 ? 'an ingredient has' : `${short} ingredients have`} no
                          price yet
                        </Text>
                      )}
                    </Stack>
                  );
                },
              },
              // The other half of the fact. A cost with nothing beside it is not a decision, and
              // the price was four clicks away on a different screen with mental arithmetic at the
              // end of them. A prep batch is made rather than sold, so it stays blank rather than
              // being given a margin against a price nobody can pay.
              {
                accessor: 'sellsForMinor',
                title: 'Sells for',
                render: (r) =>
                  typeof r.sellsForMinor === 'number' ? (
                    formatMoney(r.sellsForMinor, String(r.sellsForCurrency ?? currency))
                  ) : // An extra is sold too — "Add bacon, $2.00" — so it is asked the same
                  // question. A prep batch is made rather than sold and stays blank.
                  r.targetType === 'variant' || r.targetType === 'modifierOption' ? (
                    <Text size="sm" c="orange.7">
                      No price yet
                    </Text>
                  ) : (
                    ''
                  ),
              },
              {
                accessor: 'marginBp',
                title: 'Left after ingredients',
                /* The cost beside this one says "at least" when an ingredient has never been
                   bought. This is the same fact upside down and it was still being stated as one:
                   a margin worked out from a floor is a CEILING, and it can only come down when
                   the missing price arrives. Shown as such, and without the green — a figure that
                   is going to fall is not evidence of a healthy one. */
                render: (r) => {
                  if (typeof r.marginBp !== 'number') return '';
                  const short = typeof r.uncostedComponents === 'number' ? r.uncostedComponents : 0;
                  return (
                    <Stack gap={0}>
                      <Text size="sm" fw={600} c={short > 0 ? undefined : marginTone(r.marginBp)}>
                        {formatRateBp(r.marginBp)}
                      </Text>
                      {short > 0 && (
                        <Text size="xs" c="orange.7">
                          at most — it can only come down
                        </Text>
                      )}
                    </Stack>
                  );
                },
              },
            ]}
            rowActions={(row) => (
              <Button size="xs" variant="light" onClick={() => setEditingRecipe(row as unknown as RecipeRow)}>
                What is in it
              </Button>
            )}
          />
          {/* Under the recipes and not on a tab of its own, because this is not a subject anybody
              comes looking for: it is the thing you need in the middle of writing a recipe, at the
              moment the amount box will only accept bottles and what you meant to say was 45 ml. */}
          <UnitConversions />
        </Tabs.Panel>

        <Tabs.Panel value="ledger">
          <CrudSection
            title="Movements"
            description="Every delivery, sale, spill, move and count correction, newest first. Nothing here can be edited — a mistake is put right on the On hand tab, by counting the area again or logging waste."
            listPath={LEDGER_PATH}
            listQuery={movementsItem ? { inventoryItemId: movementsItem } : undefined}
            emptyMessage="No stock has moved yet."
            actions={
              // Without this the list is the last hundred movements of everything, and "where did
              // the rum go" cannot be asked at all.
              <Select
                size="sm"
                placeholder="Everything"
                data={items.options.map((o) => ({ value: o.value, label: o.label }))}
                value={movementsItem}
                onChange={setMovementsItem}
                clearable
                searchable
                comboboxProps={{ withinPortal: true }}
                style={{ width: 220 }}
              />
            }
            columns={[
              { accessor: 'at', title: 'When', unit: 'datetime', noWrap: true },
              { accessor: 'type', title: 'What happened', enumGroup: 'stockMovementType' },
              { accessor: 'inventoryItemId', title: 'Item', reference: 'inventoryItem' },
              // Stock is held per area, so a movement without its area cannot be reconciled against
              // anything — and the two halves of a move are otherwise identical rows.
              { accessor: 'storageLocationId', title: 'Area', reference: 'storageLocation' },
              { accessor: 'qty', title: 'Amount', render: qtyWithUnit },
              {
                accessor: 'costBaseMinor',
                title: 'Value',
                render: (r) => formatMoney(r.costBaseMinor as number, currency),
              },
              // Replaces a "Note" column that nothing has ever written to. The reason is recorded
              // against the waste itself, and this is the only screen that shows waste at all.
              {
                accessor: 'reasonCode',
                title: 'Why',
                // A note is something a person typed, so it is shown exactly as typed; a code is
                // looked up, and worded rather than printed when nothing answers to it.
                render: (r) => reasonName(r.reasonCode) || String(r.note ?? '') || '—',
              },
            ]}
          />
        </Tabs.Panel>
      </Tabs>

      {editingRecipe !== null && (
        <RecipeForm
          recipe={editingRecipe === 'new' ? null : editingRecipe}
          locationId={locationId}
          currency={currency}
          onClose={() => setEditingRecipe(null)}
        />
      )}
    </>
  );
}
